/**
 * omidJoin.js - attach OMID sessions (omidLifecycle.js) to the slot lifecycle
 * they measured. Frontend twin of proxy-server/lifecycle/omidJoin.js (the source
 * of truth; pinned by lifecycleParity.test.js + omidParity.test.js). This is the
 * LOCAL FALLBACK used by AdLifecycleView.buildSlotLifecycles when the proxy's
 * lifecycle answer is unavailable.
 *
 * There is NO hard OMID-to-slot token. The GMA correlator (`c`) is shared by
 * every slot request of an app session and real `gen_204?id=amid` beacons often
 * carry neither `c` nor `iu`, so the join is tiered and always says how it was
 * made (`joinedBy`). Decision D8 of docs/sdlc/slot-story-complete/20-requirements.md:
 *
 *   1. 'correlator'  beacon correlator === slot correlator AND, when the session
 *                    carries an iu, iu === slot adUnit. A correlator with no iu
 *                    attaches only when exactly ONE slot has that correlator.
 *                    Several same-correlator+iu slots (refresh) take the latest
 *                    one at or before the first beacon.
 *   2. 'iu_window'   first beacon at or after the slot's GAM request, under 30 s
 *                    later, and no LATER same-adUnit GAM request in between.
 *                    The GAM request time is the single anchor. When the session
 *                    has an iu it must equal the slot's.
 *   3. 'ambiguous'   more than one slot qualifies -> attaches to NONE.
 *
 * A session attaches to at most one slot; the '∅' bucket never attaches.
 * Pure, never throws.
 */

import { reconstructOmidSessions } from './omidLifecycle';

export const WINDOW_MS = 30000;

const tsOfLifecycle = (lc) => Number(lc && lc.timestamp) || 0;
const unitOfLifecycle = (lc) => String((lc && lc.adUnit) || '');

/**
 * @returns {Array<{ session, slotIndex: number, joinedBy: 'correlator'|'iu_window'|'ambiguous'|null }>}
 */
export function joinOmidSessions(lifecycles, sessions) {
  const lcs = Array.isArray(lifecycles) ? lifecycles : [];
  const list = Array.isArray(sessions) ? sessions : [];
  const out = [];
  for (const s of list) {
    try {
      out.push({ session: s, ...joinOne(lcs, s) });
    } catch {
      out.push({ session: s, slotIndex: -1, joinedBy: null });
    }
  }
  return out;
}

function joinOne(lcs, s) {
  const none = { slotIndex: -1, joinedBy: null };
  if (!s || s.attachable === false || !Array.isArray(s.events) || !s.events.length) return none;
  const corr = s.correlator || null;
  const iu = s.iu || null;
  const t = Number(s.events[0].ts) || 0;

  // Is there a later GAM request for the same ad unit that fired at or before t?
  const laterSameSlotGam = (idx) => {
    const unit = unitOfLifecycle(lcs[idx]);
    if (!unit) return false;
    const base = tsOfLifecycle(lcs[idx]);
    return lcs.some((other, j) => j !== idx && unitOfLifecycle(other) === unit
      && tsOfLifecycle(other) > base && tsOfLifecycle(other) <= t);
  };

  // ── Tier 1: correlator (+ iu agreement) ──
  if (corr) {
    const sameCorr = [];
    lcs.forEach((lc, i) => { if (lc && lc.correlator && lc.correlator === corr) sameCorr.push(i); });
    if (iu) {
      const sameIu = sameCorr.filter((i) => unitOfLifecycle(lcs[i]) === iu);
      // At-or-after guard (viewability E6): a beacon cannot precede the request it measured.
      if (sameIu.length === 1 && tsOfLifecycle(lcs[sameIu[0]]) <= t) return { slotIndex: sameIu[0], joinedBy: 'correlator' };
      if (sameIu.length === 1) return none;
      if (sameIu.length > 1) {
        // A refresh shares correlator AND iu: the latest request at or before the beacon owns it.
        const before = sameIu.filter((i) => tsOfLifecycle(lcs[i]) <= t);
        const latest = before.filter((i) => !laterSameSlotGam(i));
        if (latest.length === 1) return { slotIndex: latest[0], joinedBy: 'correlator' };
        return { slotIndex: -1, joinedBy: 'ambiguous' };
      }
      // correlator matched a slot but no iu agrees -> not a correlator join; try the window.
    } else if (sameCorr.length === 1) {
      if (tsOfLifecycle(lcs[sameCorr[0]]) <= t) return { slotIndex: sameCorr[0], joinedBy: 'correlator' };
      return none;
    }
  }

  // ── Tier 2: time window after the GAM request ──
  const cands = [];
  lcs.forEach((lc, i) => {
    if (!lc) return;
    if (iu && unitOfLifecycle(lc) !== iu) return;
    // A slot whose correlator is known and differs from the beacon's is not its slot.
    if (corr && lc.correlator && lc.correlator !== corr) return;
    const diff = t - tsOfLifecycle(lc);
    if (!(diff >= 0 && diff < WINDOW_MS)) return;
    if (laterSameSlotGam(i)) return;
    cands.push(i);
  });
  if (cands.length === 1) return { slotIndex: cands[0], joinedBy: 'iu_window' };
  if (cands.length > 1) return { slotIndex: -1, joinedBy: 'ambiguous' };
  return none;
}

/** The shape that rides on lifecycle.omidSessions[] (JSON-safe: phases is an array). */
export function shapeAttached(s, joinedBy) {
  const rowKeys = [];
  for (const e of (s.events || [])) {
    if (e && e.rowKey != null && !rowKeys.includes(e.rowKey)) rowKeys.push(e.rowKey);
  }
  return {
    key: s.key,
    vendor: s.vendor || null,
    iu: s.iu || null,
    correlator: s.correlator || null,
    phases: s.phases instanceof Set ? [...s.phases] : (Array.isArray(s.phases) ? [...s.phases] : []),
    eventCount: s.eventCount || 0,
    percentInViewMax: s.percentInViewMax == null ? null : s.percentInViewMax,
    status: s.status || 'pass',
    issues: (s.issues || []).map((i) => ({ ...i })),
    joinedBy,
    rowKeys,
  };
}

/**
 * Give every lifecycle an `omidSessions` array (possibly empty) and push each
 * joinable session onto the slot it measured. Mutates and returns `lifecycles`.
 * Deliberately does NOT touch overallStatus / issues: OMID evidence is
 * non-verdict (D6) - beacon visibility depends on pinned verification hosts.
 */
export function attachOmidSessions(lifecycles, rowsForOmid) {
  try {
    const list = Array.isArray(lifecycles) ? lifecycles : [];
    for (const lc of list) if (lc && typeof lc === 'object') lc.omidSessions = [];
    if (!list.length) return list;
    const sessions = reconstructOmidSessions(rowsForOmid);
    if (!sessions.length) return list;
    for (const j of joinOmidSessions(list, sessions)) {
      if (j.slotIndex < 0 || !j.joinedBy || j.joinedBy === 'ambiguous') continue;
      const lc = list[j.slotIndex];
      if (lc) lc.omidSessions.push(shapeAttached(j.session, j.joinedBy));
    }
    return list;
  } catch {
    return lifecycles;
  }
}
